import { useState, useEffect } from "react";
import { IconButton, Collapse, Fade } from "@mui/material";
import {
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
} from "@mui/icons-material";
import {
  IconBrandInstagram,
  IconBrandLinkedin,
  IconBrandThreads,
} from "@tabler/icons-react";

interface SectionProps {
  title: string;
  children: React.ReactNode;
  isOpen: boolean;
  toggleOpen: () => void;
  delay: number;
}

function Section({ title, children, isOpen, toggleOpen, delay }: SectionProps) {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, delay);
    return () => clearTimeout(timer);
  }, [delay]);

  return (
    <Fade in={isVisible} timeout={800}>
      <div className="mb-5 overflow-hidden rounded-2xl border border-neutral-200/80 bg-white/82 shadow-[0_18px_60px_rgba(23,23,23,0.05)] transition-smooth hover:border-primary-200 dark:border-neutral-800 dark:bg-neutral-900/70 dark:shadow-none dark:hover:border-primary-800">
        <div
          className="flex cursor-pointer items-center justify-between p-5 transition-colors hover:bg-neutral-50/80 dark:hover:bg-neutral-950/40"
          onClick={toggleOpen}
        >
          <h3 className="text-xl font-semibold tracking-tight text-neutral-950 dark:text-neutral-50">{title}</h3>
          <IconButton size="small" edge="end" sx={{ color: 'var(--primary-600)' }}>
            {isOpen ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </div>
        <Collapse in={isOpen} timeout="auto">
          <div className="border-t border-neutral-200/80 bg-white/70 p-5 prose prose-neutral max-w-none dark:prose-invert dark:border-neutral-800 dark:bg-neutral-900/40">
            {children}
          </div>
        </Collapse>
      </div>
    </Fade>
  );
}

export default function About() {
  const [expandedSections, setExpandedSections] = useState<
    Record<string, boolean>
  >({
    principles: true,
    crafts: false,
    improving: false,
    leadership: false,
    podcasts: false,
    family: false,
  });

  const allExpanded = Object.values(expandedSections).every((value) => value);

  const toggleAllSections = () => {
    const newValue = !allExpanded;
    setExpandedSections({
      principles: newValue,
      crafts: newValue,
      improving: newValue,
      leadership: newValue,
      podcasts: newValue,
      family: newValue,
    });
  };

  const toggleSection = (section: string) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const socialLinks = [
    {
      label: "LinkedIn",
      href: "https://www.linkedin.com/in/arvindkc",
      icon: IconBrandLinkedin,
    },
    {
      label: "Instagram",
      href: "https://www.instagram.com/kc_arvind/",
      icon: IconBrandInstagram,
    },
    {
      label: "Threads",
      href: "https://www.threads.com/@kc_arvind",
      icon: IconBrandThreads,
    },
  ];

  return (
    <div className="mx-auto max-w-4xl px-4 py-12 animate-fade-in">
      <div className="mb-12 text-center">
        <Fade in={true} timeout={1000}>
          <div>
            <div className="mb-8 flex justify-center">
              <div className="relative group">
                <img
                  src="/images/about-kc.png"
                  alt="KC"
                  className="relative h-40 w-40 rounded-full border border-neutral-200 object-cover shadow-[0_18px_70px_rgba(23,23,23,0.12)] dark:border-neutral-800"
                />
              </div>
            </div>

            <h1 className="text-4xl font-semibold tracking-tight text-neutral-950 dark:text-neutral-50">
              About KC
            </h1>
          </div>
        </Fade>
      </div>

      <div className="mb-10 flex flex-col items-center gap-4">
        <button
          onClick={toggleAllSections}
          className="inline-flex items-center rounded-full border border-primary-300 bg-primary-600 px-5 py-2.5 text-sm font-medium text-white shadow-[0_12px_32px_rgba(124,58,237,0.18)] transition-smooth hover:border-primary-500 hover:bg-primary-700 active:translate-y-px dark:border-primary-700 dark:bg-primary-500 dark:hover:bg-primary-400"
        >
          {allExpanded ? "Collapse All" : "Expand All"}
        </button>

        <div className="flex flex-wrap justify-center gap-3">
          {socialLinks.map(({ label, href, icon: Icon }) => (
            <a
              key={label}
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-full border border-neutral-200/90 bg-white/88 px-4 py-2.5 text-sm font-medium text-neutral-700 shadow-[0_10px_30px_rgba(23,23,23,0.05)] transition-smooth hover:-translate-y-0.5 hover:border-primary-300 hover:text-primary-700 hover:shadow-[0_14px_34px_rgba(124,58,237,0.10)] active:translate-y-px dark:border-neutral-800 dark:bg-neutral-900/78 dark:text-neutral-200 dark:hover:border-primary-700 dark:hover:text-primary-300"
            >
              <Icon size={18} stroke={1.8} />
              <span>{label}</span>
            </a>
          ))}
        </div>
      </div>

      <div className="space-y-6">
        <Section
          title="Principles I believe in"
          isOpen={expandedSections.principles}
          toggleOpen={() => toggleSection("principles")}
          delay={100}
        >
          <ul className="list-disc pl-5 space-y-3">
            <li className="text-neutral-700 dark:text-neutral-300">
              Seek{" "}
              <span className="font-semibold text-primary-700 dark:text-primary-300">fulfillment</span>{" "}
              which often comes through hard choices vs. pleasure which comes
              through easy choices.
            </li>
            <li className="text-neutral-700 dark:text-neutral-300">
              <span className="font-semibold text-primary-700 dark:text-primary-300">
                Love, service to others and mastery
              </span>{" "}
              are the main motivators in life. Work is an excellent conduit for
              all three.
            </li>
            <li className="text-neutral-700 dark:text-neutral-300">
              Always be{" "}
              <span className="font-semibold text-primary-700 dark:text-primary-300">learning</span>.
              You start aging when you stop learning or when the curve flattens.
            </li>
            <li className="text-neutral-700 dark:text-neutral-300">
              Don't trade{" "}
              <span className="font-semibold text-primary-700 dark:text-primary-300">
                health for wealth
              </span>{" "}
              and then wealth for health. In other words, work with intensity
              but not at the cost of good sleep and exercise.
            </li>
            <li className="text-neutral-700 dark:text-neutral-300">
              In each situation, seek what you can{" "}
              <span className="font-semibold text-primary-700 dark:text-primary-300">give</span> and
              not what you can get.
            </li>
            <li className="text-neutral-700 dark:text-neutral-300">
              Be deeply{" "}
              <span className="font-semibold text-primary-700 dark:text-primary-300">grateful</span>{" "}
              for the opportunity that you have.
            </li>
          </ul>
        </Section>

        <Section
          title="Crafts that I pursue mastery in"
          isOpen={expandedSections.crafts}
          toggleOpen={() => toggleSection("crafts")}
          delay={200}
        >
          <p className="leading-relaxed text-neutral-700 dark:text-neutral-300">
            I see myself as a{" "}
            <span className="font-semibold text-primary-700 dark:text-primary-300">technologist</span>{" "}
            and as an{" "}
            <span className="font-semibold text-primary-700 dark:text-primary-300">
              organization builder
            </span>{" "}
            and these are crafts that I deliberately practice. Specific to
            technology, I find time every day to learn something, understand a
            hard concept and to write some code. To deepen my organization
            building skills, in addition to reading and designing executive
            coaching programs, I am writing a book on leadership. The groundwork
            needed for writing has served to deepen my mental models on
            leadership.
          </p>
        </Section>

        <Section
          title="Things that I am getting better at"
          isOpen={expandedSections.improving}
          toggleOpen={() => toggleSection("improving")}
          delay={300}
        >
          <p className="leading-relaxed text-neutral-700 dark:text-neutral-300">
            Currently, I am focused on{" "}
            <span className="font-semibold text-primary-700 dark:text-primary-300">
              Machine Learning (ML)
            </span>{" "}
            and have been building ML models and deeply researching how
            organizations can be successful in their ML journey.
          </p>
        </Section>

        <Section
          title="My view of leadership"
          isOpen={expandedSections.leadership}
          toggleOpen={() => toggleSection("leadership")}
          delay={400}
        >
          <p className="leading-relaxed text-neutral-700 dark:text-neutral-300">
            I believe the main role of a leader is to{" "}
            <span className="font-semibold text-primary-700 dark:text-primary-300">
              enable people to rise to their highest potential
            </span>
            . My thoughts on how to do it is captured in my{" "}
            <a
              href="https://www.linkedin.com/pulse/yikes-i-need-lead-people-arvind-kc/"
              className="text-blue-600 hover:underline font-medium"
              target="_blank"
              rel="noopener noreferrer"
            >
              LinkedIn article
            </a>
            .
          </p>
        </Section>

        <Section
          title="Podcast appearances"
          isOpen={expandedSections.podcasts}
          toggleOpen={() => toggleSection("podcasts")}
          delay={500}
        >
          <div className="space-y-4">
            <p className="text-neutral-700 dark:text-neutral-300">
              Listen to my thoughts on leadership, engineering, and
              organizational culture:
            </p>
            <div className="grid gap-4 md:grid-cols-2">
              <a
                href="https://www.youtube.com/watch?v=QKxtR87AGZ8"
                className="block rounded-xl border border-neutral-200 p-4 transition-smooth hover:border-primary-200 hover:bg-primary-50/60 dark:border-neutral-800 dark:hover:border-primary-800 dark:hover:bg-primary-950/20"
                target="_blank"
                rel="noopener noreferrer"
              >
                <h4 className="font-medium text-primary-700 dark:text-primary-300">YouTube Interview</h4>
                <p className="mt-1 text-sm text-neutral-600 dark:text-neutral-400">
                  Discussion about engineering leadership and building
                  high-performing teams
                </p>
              </a>

              <a
                href="https://mirasee.com/blog/podcasts/leadership-as-a-craft-arvind-kc/"
                className="block rounded-xl border border-neutral-200 p-4 transition-smooth hover:border-primary-200 hover:bg-primary-50/60 dark:border-neutral-800 dark:hover:border-primary-800 dark:hover:bg-primary-950/20"
                target="_blank"
                rel="noopener noreferrer"
              >
                <h4 className="font-medium text-primary-700 dark:text-primary-300">
                  To Lead Is Human Podcast
                </h4>
                <p className="mt-1 text-sm text-neutral-600 dark:text-neutral-400">
                  Deep dive into leadership principles and practices
                </p>
              </a>

              <a
                href="https://share.transistor.fm/s/7f7f052c"
                className="block rounded-xl border border-neutral-200 p-4 transition-smooth hover:border-primary-200 hover:bg-primary-50/60 dark:border-neutral-800 dark:hover:border-primary-800 dark:hover:bg-primary-950/20"
                target="_blank"
                rel="noopener noreferrer"
              >
                <h4 className="font-medium text-primary-700 dark:text-primary-300">Roblox Tech Talks</h4>
                <p className="mt-1 text-sm text-neutral-600 dark:text-neutral-400">
                  Insights on building scalable platforms and engineering
                  culture
                </p>
              </a>
            </div>
          </div>
        </Section>

        <Section
          title="Family and fun"
          isOpen={expandedSections.family}
          toggleOpen={() => toggleSection("family")}
          delay={600}
        >
          <ul className="list-disc pl-5 space-y-3">
            <li className="text-neutral-700 dark:text-neutral-300">
              In my spare time, I love to{" "}
              <span className="font-semibold text-primary-700 dark:text-primary-300">hike</span>, visit{" "}
              <span className="font-semibold text-primary-700 dark:text-primary-300">
                National Parks
              </span>{" "}
              and play
              <span className="font-semibold text-primary-700 dark:text-primary-300"> tennis</span>
            </li>
            <li className="text-neutral-700 dark:text-neutral-300">
              I have won as many{" "}
              <span className="font-semibold text-primary-700 dark:text-primary-300">
                Tour de France
              </span>{" "}
              cups as Lance Armstrong.
            </li>
          </ul>
        </Section>
      </div>
    </div>
  );
}
